import { Button } from "@/components/ui/button";
import { Menu, Settings2, Sparkles, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, subscription } = useAuth();
  const hasActiveSub = subscription.subscribed;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Features", href: "/features" },
    { label: "Preise", href: "/preise" },
    { label: "Konfigurator", href: "/konfigurator" },
    { label: "Branchen", href: "/branchen" },
    { label: "Hilfe", href: "/hilfe" },
    { label: "Über uns", href: "/ueber-uns" },
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "border-b border-border/50 bg-background/80 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="container flex h-20 items-center justify-between">
        <Link to="/" className="group flex items-center">
          <img src="/favicon.svg" alt="NextInvoice" className="h-9 transition-transform duration-300 group-hover:scale-105" />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className={`relative px-4 py-2 text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {item.label}
              {isActive(item.href) && (
                <span className="absolute bottom-0 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full bg-accent" />
              )}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <Button
            variant="outline"
            size="sm"
            className="border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary"
            asChild
          >
            <Link to={user ? "/portal/dashboard" : "/portal/login"}>
              {user ? "Portal" : "Anmelden"}
            </Link>
          </Button>
          {hasActiveSub ? (
            <Button
              size="sm"
              className="group relative overflow-hidden bg-accent px-6 text-accent-foreground shadow-lg shadow-accent/25 transition-all duration-300 hover:bg-accent hover:shadow-xl hover:shadow-accent/30"
              asChild
            >
              <Link to="/konfigurator">
                <Settings2 className="mr-2 h-4 w-4" />
                Konfigurator
              </Link>
            </Button>
          ) : (
            <Button
              size="sm"
              className="group relative overflow-hidden bg-accent px-6 text-accent-foreground shadow-lg shadow-accent/25 transition-all duration-300 hover:bg-accent hover:shadow-xl hover:shadow-accent/30"
              asChild
            >
              <Link to="/preise">
                <Sparkles className="mr-2 h-4 w-4" />
                Kostenlos testen
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
              </Link>
            </Button>
          )}
        </div>

        <button
          className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-card lg:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="absolute left-0 right-0 top-full border-b border-border bg-background/95 backdrop-blur-xl lg:hidden">
          <nav className="container flex flex-col gap-1 py-4">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`rounded-xl px-4 py-3 text-left text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-3 flex flex-col gap-2 border-t border-border pt-4">
              <Button variant="ghost" className="justify-start" asChild>
                <Link to={user ? "/portal/dashboard" : "/portal/login"} onClick={() => setMobileMenuOpen(false)}>
                  {user ? "Portal" : "Anmelden"}
                </Link>
              </Button>
              <Button className="bg-accent text-accent-foreground" asChild>
                <Link to={hasActiveSub ? "/konfigurator" : "/preise"} onClick={() => setMobileMenuOpen(false)}>
                  {hasActiveSub ? (
                    <><Settings2 className="mr-2 h-4 w-4" />Konfigurator</>
                  ) : (
                    <><Sparkles className="mr-2 h-4 w-4" />Kostenlos testen</>
                  )}
                </Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
